import { useState } from "react";
import { BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { Share2, Link2, Check, Twitter, Facebook, MessageCircle, Code2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

export default function ShareButton({ collection }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copiedEmbed, setCopiedEmbed] = useState(false);

  const shareUrl = `${BACKEND_URL}/api/collections/${collection.id}/share`;
  const embedUrl = `${BACKEND_URL}/api/collections/${collection.id}/embed`;
  const embedCode = `<iframe src="${embedUrl}" width="100%" height="640" style="border:0;border-radius:12px" loading="lazy" title="${collection.title}"></iframe>`;
  const text = `"${collection.title}" — a curated collection on Archive Nepal`;

  const onClick = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: collection.title, text, url: shareUrl });
        return;
      } catch (e) { /* user cancelled or unsupported → fall back to dialog */ }
    }
    setOpen(true);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { toast.error("Could not copy"); }
  };

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopiedEmbed(true);
      toast.success("Embed code copied");
      setTimeout(() => setCopiedEmbed(false), 2000);
    } catch (e) { toast.error("Could not copy"); }
  };

  const socials = [
    { name: "X", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`, cls: "hover:border-ink hover:text-ink" },
    { name: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, cls: "hover:border-blue-600 hover:text-blue-600" },
    { name: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(text + " " + shareUrl)}`, cls: "hover:border-emerald-600 hover:text-emerald-600" },
  ];

  return (
    <>
      <button
        data-testid="share-button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 bg-brick hover:bg-brick-dark text-parchment px-4 py-2 rounded-full text-sm font-semibold transition-colors"
      >
        <Share2 className="h-4 w-4" /> Share
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="share-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Share this collection</DialogTitle>
            <DialogDescription>Anyone with the link can view this published collection.</DialogDescription>
          </DialogHeader>

          {collection.cover_image && (
            <div className="rounded-lg overflow-hidden border border-stoneline">
              <img src={collection.cover_image} alt="" className="w-full h-40 object-cover" />
              <div className="p-3 bg-white">
                <p className="font-serif font-semibold text-ink line-clamp-1">{collection.title}</p>
                <p className="text-xs text-muted-foreground">archivenepal.org · Digital Collections</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 border border-stoneline rounded-full pl-4 pr-1 py-1">
            <span data-testid="share-link-text" className="text-sm text-muted-foreground truncate flex-1">{shareUrl}</span>
            <button data-testid="copy-link-btn" onClick={copy} className="shrink-0 inline-flex items-center gap-1.5 bg-brick hover:bg-brick-dark text-parchment px-3 py-1.5 rounded-full text-sm font-semibold transition-colors">
              {copied ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {copied ? "Copied" : "Copy"}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2">
            {socials.map((s) => (
              <a
                key={s.name}
                href={s.href}
                target="_blank"
                rel="noreferrer"
                data-testid={`share-${s.name.toLowerCase()}`}
                className={`flex flex-col items-center gap-1.5 border border-stoneline rounded-lg py-3 text-sm font-medium text-ink transition-colors ${s.cls}`}
              >
                <s.icon className="h-5 w-5" /> {s.name}
              </a>
            ))}
          </div>

          <div className="border-t border-stoneline pt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-semibold text-ink flex items-center gap-1.5"><Code2 className="h-4 w-4" /> Embed on your site</span>
              <button data-testid="copy-embed-btn" onClick={copyEmbed} className="inline-flex items-center gap-1.5 text-brick text-sm font-semibold hover:underline">
                {copiedEmbed ? <Check className="h-4 w-4" /> : <Link2 className="h-4 w-4" />} {copiedEmbed ? "Copied" : "Copy code"}
              </button>
            </div>
            <textarea
              data-testid="embed-code"
              readOnly
              onFocus={(e) => e.target.select()}
              value={embedCode}
              rows={3}
              className="w-full text-xs font-mono bg-stone-50 border border-stoneline rounded-lg p-2 text-ink/80 resize-none outline-none focus:ring-2 focus:ring-brick/30"
            />
            <p className="text-xs text-muted-foreground mt-1">Paste this into any webpage to embed the collection.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
